import { useEffect, useRef, useState } from "react";
import { Form } from "antd";
import { addDays } from "date-fns";
import moment from "moment";
import { MdChevronLeft, MdChevronRight, MdViewModule, MdViewWeek, MdToday, MdCalendarToday, MdPictureAsPdf } from "react-icons/md";
import {
    Container,
    RowField,
    SelectAntd,
    Header,
    HeaderFilters,
    CalendarNav,
    Button,
    ButtonToday,
    CalendarTitle,
    RangeCalendarDropdown,
    ViewDropdown,
    AgendaTypeSelector,
    AgendaTypeButton,
    ContentLabel,
    Content,
    AgendaTypeButtonExport
} from "./styles";
import { FiltrosAvancado } from "../../interfaces/Planejamento";
import { useFilters } from "core/features/filters/store";
import Calendar from "./components/Calendar";
import { HeaderStatus } from "./components/HeaderStatus";
import ModalDetail from "./components/ModalDetail";
import { useSheduleDetailExport, useSheduleJanelaExecucaoList, useSheduleTalhaoList, useSheduleTradicionalList } from "../../hooks/useSchedule";
import RangeCalendar, { RangeCalendarValue } from "./components/RangeCalendar";
import { addMonths, dayOfMonth } from "./utils";
import ModalExportPdf from "./components/ModalExportPdf";
import { useValidatePermission } from "core/features/auth/hooks/useValidatePermission";
import useAgendaCampo from "../../hooks/useAgendaCampo";
import { Loading } from "./Loading";

type Props = {
    filtrosAvancados?: FiltrosAvancado;
};

type ViewMode = "month" | "week" | "talhao" | "janela-execucao";


function AgendaCampo({ filtrosAvancados }: Props) {
    const { Item } = Form;
    const { hasPermission } = useValidatePermission();
    const { viewMode, setViewMode, setListWindowExecution } = useAgendaCampo();
    const [monthOffset, setMonthOffset] = useState(0);
    const [weekOffset, setWeekOffset] = useState(0);
    const [schedule, setSchedule] = useState('tradicional');
    const [optionMonthOrWeek, setOptionMonthOrWeek] = useState('month');
    const [isRangeCalendarOpen, setIsRangeCalendarOpen] = useState(false);
    const [selectedRange, setSelectedRange] = useState<RangeCalendarValue | null>(null);

    const [statusPlanejado, setStatusPlanejado] = useState(true);
    const [statusExecutadoParcialmente, setStatusExecutadoParcialmente] = useState(true);
    const [statusExecutado, setStatusExecutado] = useState(true);
    const [statusAtrasado, setStatusAtrasado] = useState(true);
    const [pdfUrl, _] = useState<string | null>(null);

    const calendarTitleRef = useRef<HTMLDivElement | null>(null);
    const rangeDropdownRef = useRef<HTMLDivElement | null>(null);

    const { filters, updateField } = useFilters();
    const { unidade, safra, filtersAdvanced, tabBarSessaoPlanejamentoOperacional } = filters;

    const list = [
        { label: <ContentLabel><MdViewModule /> Mensal</ContentLabel>, value: 'month' },
        { label: <ContentLabel><MdViewWeek /> Semanal</ContentLabel>, value: 'week' },
    ];

    const periodoProd = filtrosAvancados?.safraPeriodos.find((item: any) => item.safraPeriodoId === filtersAdvanced.safraPeriodo);

    const query = {
        DataPeriodoInicial: formatShowDate().start,
        DataPeriodoFim: formatShowDate().end,
        UnidadeId: unidade?.id,
        SafraId: safra?.id,
        SafraPeriodoIds: filters.filtersAdvanced.safraPeriodo,
        OperacaoClasseIds: filters.filtersAdvanced.classeOperacao,
    };

    const { data: listSheduleTradicional, isFetching: isFetchingSheduleTradicional } = useSheduleTradicionalList(query, schedule === 'tradicional');
    const { data: listSheduleTalhao, isFetching: isFetchingSheduleTalhao } = useSheduleTalhaoList(query, schedule === 'talhao');
    const { data: listSheduleJanelaExecucao, isFetching: isFetchingSheduleJanelaExecucao } = useSheduleJanelaExecucaoList(query, schedule === 'janela-execucao');
    const { mutateAsync: exportShedule } = useSheduleDetailExport();

    useEffect(() => {
        if (viewMode === 'janela-execucao' && listSheduleJanelaExecucao) {
            setListWindowExecution(listSheduleJanelaExecucao);
        }
    }, [listSheduleJanelaExecucao]);

    useEffect(() => {
        setSchedule('tradicional');
        setOptionMonthOrWeek('month');
        setViewMode(optionMonthOrWeek as ViewMode);
        setMonthOffset(0);
        setWeekOffset(0);
    }, [tabBarSessaoPlanejamentoOperacional]);

    useEffect(() => {
        if (!isRangeCalendarOpen) return;

        const handleDocumentMouseDown = (e: MouseEvent) => {
            const target = e.target as Node;

            if (calendarTitleRef.current?.contains(target)) return;

            setIsRangeCalendarOpen(false);
        };

        document.addEventListener("mousedown", handleDocumentMouseDown);
        return () => document.removeEventListener("mousedown", handleDocumentMouseDown);
    }, [isRangeCalendarOpen]);

    const isMonthGranularity = viewMode === 'month' || viewMode === 'janela-execucao';
    const currentOffset = isMonthGranularity ? monthOffset : weekOffset;

    const toDay = addMonths(new Date(), monthOffset);

    let month = toDay.toLocaleString('pt-BR', { month: 'long' });

    month = month.charAt(0).toUpperCase() + month.slice(1);

    function formatShowDate() {

        const dates = selectedRange;

        const today = new Date();
        const yearCurrent = today.getFullYear();
        const monthCurrent = today.getMonth() + 1;

        const start: Date = dates ? dates?.start?.toDate() : new Date(`${yearCurrent}-${monthCurrent}-01`);
        const counter = viewMode === 'month' || viewMode === 'janela-execucao' ? dayOfMonth(start) - 1 : 6;
        const end: Date = addDays(selectedRange ? selectedRange?.start?.toDate() : new Date(`${yearCurrent}-${monthCurrent}-01`), counter); // dates?.end?.toDate();

        let month = '';

        const yearStart = start.getFullYear();
        const monthStart = String(start.getMonth() + 1).padStart(2, '0');
        const dayStart = String(start.getDate()).padStart(2, '0');

        const yearEnd = end.getFullYear();
        const monthEnd = String(end.getMonth() + 1).padStart(2, '0');
        const dayEnd = String(end.getDate()).padStart(2, '0');

        let month01 = start.toLocaleString('pt-BR', { month: 'long' });
        let month02 = end.toLocaleString('pt-BR', { month: 'long' });

        month01 = month01.charAt(0).toUpperCase() + month01.slice(1);
        month02 = month02.charAt(0).toUpperCase() + month02.slice(1);

        if (month01 !== month02) {
            month = `${month01} - ${month02}`;
        } else {
            month = month01;
        }

        let str = `${month} ${yearStart}`

        if (viewMode !== 'month' && viewMode !== 'janela-execucao') {
            str = `${dayStart}/${monthStart} - ${dayEnd}/${monthEnd}`
        }

        const res = {
            start: `${yearStart}-${monthStart}-${dayStart}`,
            end: `${yearEnd}-${monthEnd}-${dayEnd}`,
            month: str,
        };

        return res;
    }

    function buildRangeForOffset(offset: number, monthGranularity: boolean): RangeCalendarValue {
        const now = moment();
        const str = monthGranularity
            ? now.startOf("month").add(offset, 'month')
            : now.startOf("week").add(offset, 'week');

        return {
            start: str,
            end: str
        };
    }

    function handleSetData(offset: number) {
        if (isMonthGranularity) {
            setMonthOffset(offset);
        } else {
            setWeekOffset(offset);
        }

        setSelectedRange(buildRangeForOffset(offset, isMonthGranularity));
    }

    async function handleExportPDF() {
        const blob = await exportShedule(query);
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "arquivo.pdf";
        link.click();
    }

    function getDefaultValue() {
        const filtro = filtersAdvanced?.safraPeriodo;
        const filtros = filtrosAvancados?.safraPeriodos;
        if (!filtros?.some(f => f.safraPeriodoId === filtro)) {
            return undefined;
        }
        return filtro;
    }

    return (
        <Container>
            <Header>
                <HeaderFilters>
                    <div style={{ width: 280 }}>
                        <Item
                            label="Período de Produção"
                            name="periodoProducao"
                            rules={[{ required: true, message: "Período de Produção é obrigatório" }]}
                        >
                            <SelectAntd
                                onChange={(dataValue: any) => {
                                    updateField("filtersAdvanced", {
                                        ...filtersAdvanced,
                                        safraPeriodo: dataValue
                                    });
                                }}
                                placeholder="Período de Produção"
                                defaultValue={getDefaultValue()}
                                disabled={!hasPermission(['AgendaCampo.Visualizar'])}
                                options={(filtrosAvancados?.safraPeriodos || [])
                                    ?.map((item: any) => ({
                                        label: item.nome,
                                        value: item.safraPeriodoId,
                                    }))}
                            />
                        </Item>
                    </div>
                    <div style={{ width: 280 }}>
                        <Item
                            label="Classe de Operação"
                            name="classeOperacao"
                        >
                            <SelectAntd
                                allowClear
                                showSearch
                                mode="multiple"
                                maxTagCount="responsive"
                                placeholder="Classe de Operação"
                                optionFilterProp="label"
                                disabled={!hasPermission(['AgendaCampo.Visualizar'])}
                                defaultValue={filtersAdvanced?.classeOperacao}
                                onChange={(dataValue: any) => {
                                    updateField("filtersAdvanced", {
                                        ...filtersAdvanced,
                                        classeOperacao: dataValue,
                                    });
                                }}
                                options={(filtrosAvancados?.operacaoClasses || [])
                                    ?.map((item: any) => ({
                                        label: item.descricao,
                                        value: item.operacaoClasseId,
                                    }))}
                                maxTagPlaceholder={(omittedValues) => (
                                    <span>+{omittedValues.length}</span>
                                )}
                            />
                        </Item>
                    </div>
                </HeaderFilters>
                <CalendarNav>
                    <ButtonToday onClick={() => handleSetData(0)} disabled={!hasPermission(['AgendaCampo.Visualizar'])}><MdToday />Hoje</ButtonToday>
                    {
                        schedule === 'tradicional' && (
                            <ViewDropdown>
                                <SelectAntd
                                    placeholder={<ContentLabel><MdCalendarToday /> Mensal</ContentLabel>}
                                    options={list}
                                    disabled={!hasPermission(['AgendaCampo.Visualizar'])}
                                    onChange={(value) => {
                                        setViewMode(value as ViewMode);
                                        setOptionMonthOrWeek(value as string);
                                        const monthGranularity = value === 'month';
                                        const offset = monthGranularity ? monthOffset : weekOffset;
                                        setSelectedRange(buildRangeForOffset(offset, monthGranularity));
                                    }}
                                />
                            </ViewDropdown>
                        )
                    }
                    <Button disabled={!hasPermission(['AgendaCampo.Visualizar'])} onClick={() => handleSetData(currentOffset - 1)}><MdChevronLeft /></Button>

                    <CalendarTitle
                        ref={calendarTitleRef}
                        onClick={() => {
                            if (!hasPermission(['AgendaCampo.Visualizar'])) return;
                            setIsRangeCalendarOpen((prev) => !prev);
                        }}
                    >
                        <span>{selectedRange ? formatShowDate().month : `${month} ${toDay.getFullYear()}`}</span>
                        {isRangeCalendarOpen && (
                            <RangeCalendarDropdown
                                ref={rangeDropdownRef}
                                onClick={(e) => e.stopPropagation()}
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                }}
                            >
                                <RangeCalendar
                                    value={selectedRange}
                                    onChange={setSelectedRange}
                                    allowRangeSelection={false}
                                    onRangeDone={(range) => {
                                        setSelectedRange(range);
                                        setIsRangeCalendarOpen(false);
                                    }}

                                />
                            </RangeCalendarDropdown>
                        )}
                    </CalendarTitle>
                    <Button disabled={!hasPermission(['AgendaCampo.Visualizar'])} onClick={() => handleSetData(currentOffset + 1)}><MdChevronRight /></Button>

                </CalendarNav>
                <AgendaTypeSelector>
                    <AgendaTypeButton
                        onClick={() => {
                            setSchedule('tradicional');
                            const nextViewMode = optionMonthOrWeek as ViewMode;
                            setViewMode(nextViewMode);
                            const monthGranularity = nextViewMode === 'month';
                            const offset = monthGranularity ? monthOffset : weekOffset;
                            setSelectedRange(buildRangeForOffset(offset, monthGranularity));
                        }}
                        active={schedule === 'tradicional'}
                        disabled={!hasPermission(['AgendaCampo.Visualizar'])}
                    >Agenda Tradicional</AgendaTypeButton>
                    <AgendaTypeButton
                        onClick={() => {
                            setSchedule('talhao');
                            setViewMode('talhao');
                            setSelectedRange(buildRangeForOffset(weekOffset, false));
                        }}
                        active={schedule === 'talhao'}
                        disabled={!hasPermission(['AgendaCampo.Visualizar'])}
                    >Agenda Talhão</AgendaTypeButton>
                    <AgendaTypeButton
                        onClick={() => {
                            setSchedule('janela-execucao');
                            setViewMode('janela-execucao');
                            setSelectedRange(buildRangeForOffset(monthOffset, true));
                        }}
                        active={schedule === 'janela-execucao'}
                        disabled={!hasPermission(['AgendaCampo.Visualizar'])}
                    >Janela de Execução</AgendaTypeButton>
                    <AgendaTypeButtonExport disabled={!hasPermission(['AgendaCampo.Visualizar'])} onClick={handleExportPDF}><MdPictureAsPdf /> Exportar PDF</AgendaTypeButtonExport>
                </AgendaTypeSelector>
            </Header>

            <Content>

                <RowField>
                    <div style={{ width: "100%" }}>
                        {
                            schedule === 'talhao' && (
                                <HeaderStatus
                                    status={
                                        {
                                            statusPlanejado,
                                            statusExecutadoParcialmente,
                                            statusExecutado,
                                            statusAtrasado,
                                            setStatusPlanejado,
                                            setStatusExecutadoParcialmente,
                                            setStatusExecutado,
                                            setStatusAtrasado
                                        }
                                    }
                                />
                            )
                        }
                        {
                            filtersAdvanced?.safraPeriodo ? (
                                <>
                                    {
                                        isFetchingSheduleTradicional || isFetchingSheduleTalhao || isFetchingSheduleJanelaExecucao ? <Loading /> : (

                                            <Calendar
                                                viewMode={viewMode}
                                                currentDate={formatShowDate().start}
                                                data={
                                                    !hasPermission(['AgendaCampo.Visualizar'])
                                                        ? []
                                                        : viewMode === 'talhao' ? listSheduleTalhao : viewMode === 'janela-execucao' ? listSheduleJanelaExecucao : listSheduleTradicional || []}
                                                status={
                                                    {
                                                        statusPlanejado,
                                                        statusExecutadoParcialmente,
                                                        statusExecutado,
                                                        statusAtrasado
                                                    }
                                                }
                                            />
                                        )
                                    }
                                </>

                            ) : <p style={{ width: "100%", height: "70vh", display: "flex", justifyContent: "center", alignItems: "center" }}>Selecione um período de produção para exibir o calendário</p>
                        }
                    </div>
                </RowField>
            </Content>
            <ModalDetail queryParams={query} schedule={schedule} periodoProducao={periodoProd?.nome || ''} />
            <ModalExportPdf pdfUrl={pdfUrl} />
        </Container>
    )
}

export default AgendaCampo;